/**
 * One user's access, contract terms and credits, for the admin: which
 * products they hold on which plan (grant, change plan, revoke), the terms a
 * contract sets on a row (fee, included credits, discount, overage), their
 * balance, and a signed adjustment with a note. Sits under a user's row on
 * Settings ▸ Users.
 *
 * Grants write the SAME rows Stripe will, in Stripe's vocabulary, so a
 * hand-granted product and a bought one are indistinguishable downstream.
 * Adjustments BANK (never expire) so a comp is not lost at the period roll.
 * "Mark overage invoiced" is the same ADJUST, sized to bring the bank to zero.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { formatCredits, resetsOn } from '../../billing/credits';
import { PRODUCT_INFO, PRODUCT_ORDER } from '../../shell/products';
import type {
  AdminCatalogRow,
  AdminCredits,
  AdminEntitlement,
  AdminEntitlementGrant,
  EntitlementTerms,
  PlanKey,
  Product,
} from '../../types';

const PLAN_KEYS: PlanKey[] = ['free', 'starter', 'pro'];

const NUM = 'w-24 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-0.5 text-xs text-right tabular-nums';

function TermsEditor({
  row,
  onSave,
  busy,
}: {
  row: AdminEntitlement;
  onSave: (patch: Partial<EntitlementTerms>, clear: (keyof EntitlementTerms)[]) => Promise<void>;
  busy: boolean;
}) {
  const [fee, setFee] = useState(row.monthly_fee_usd?.toString() ?? '');
  const [allowance, setAllowance] = useState(row.allowance_override?.toString() ?? '');
  const [mult, setMult] = useState(String(row.rate_multiplier ?? 1));
  const [policy, setPolicy] = useState(row.overage_policy ?? 'STOP');
  const [cap, setCap] = useState(row.overage_cap_credits?.toString() ?? '');
  const [rate, setRate] = useState(row.overage_usd_per_credit?.toString() ?? '');

  const save = async () => {
    const patch: Partial<EntitlementTerms> = { rate_multiplier: Number(mult) || 1, overage_policy: policy };
    const clear: (keyof EntitlementTerms)[] = [];
    if (fee.trim()) patch.monthly_fee_usd = Number(fee);
    else clear.push('monthly_fee_usd');
    if (allowance.trim()) patch.allowance_override = Number(allowance);
    else clear.push('allowance_override');
    if (cap.trim()) patch.overage_cap_credits = Number(cap);
    else clear.push('overage_cap_credits');
    if (rate.trim()) patch.overage_usd_per_credit = Number(rate);
    else clear.push('overage_usd_per_credit');
    await onSave(patch, clear);
  };

  return (
    <div className="mt-1 rounded border border-dashed border-gray-300 dark:border-gray-600 p-2 grid gap-1 text-xs" data-testid="terms-editor">
      <label className="flex items-center justify-between gap-2">
        <span>Billed $ / month</span>
        <input type="number" min="0" step="1" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="plan" className={NUM} />
      </label>
      <label className="flex items-center justify-between gap-2">
        <span>Included credits / month</span>
        <input type="number" min="0" step="100" value={allowance} onChange={(e) => setAllowance(e.target.value)} placeholder="plan" className={NUM} />
      </label>
      <label className="flex items-center justify-between gap-2">
        <span title="Multiplies the rate card: 0.8 = 20% off">Rate multiplier</span>
        <input type="number" min="0.01" step="0.05" value={mult} onChange={(e) => setMult(e.target.value)} className={NUM} />
      </label>
      <label className="flex items-center justify-between gap-2">
        <span>At zero</span>
        <select
          value={policy}
          onChange={(e) => setPolicy(e.target.value as EntitlementTerms['overage_policy'])}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1 py-0.5 text-xs"
        >
          <option value="STOP">Stop</option>
          <option value="BILL">Keep going, bill the overage</option>
        </select>
      </label>
      {policy === 'BILL' && (
        <>
          <label className="flex items-center justify-between gap-2">
            <span>Overage cap (credits)</span>
            <input type="number" min="0" step="100" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="unlimited" className={NUM} />
          </label>
          <label className="flex items-center justify-between gap-2">
            <span>Overage $ / credit</span>
            <input type="number" min="0" step="0.001" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="price book" className={NUM} />
          </label>
        </>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={save}
        className="justify-self-end px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
      >
        Save terms
      </button>
    </div>
  );
}

export default function UserAccessPanel({ userId }: { userId: number }) {
  const [rows, setRows] = useState<AdminEntitlement[] | null>(null);
  const [catalog, setCatalog] = useState<AdminCatalogRow[]>([]);
  const [credits, setCredits] = useState<AdminCredits | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [termsOpen, setTermsOpen] = useState<Product | null>(null);

  const load = useCallback(async () => {
    try {
      const [ents, cr, cat] = await Promise.all([
        api.adminListEntitlements(userId),
        api.adminGetCredits(userId),
        api.adminGetCatalog(),
      ]);
      setRows(ents);
      setCredits(cr);
      setCatalog(cat);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load access');
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const adjust = async () => {
    const n = Number(amount);
    if (!n || !note.trim()) return;
    await act(() => api.adminAdjustCredits(userId, n, note.trim()));
    setAmount('');
    setNote('');
  };

  const byProduct = new Map((rows ?? []).map((r) => [r.product, r]));
  // EVERY PRODUCT THEY HOLD, not only the ones still on sale. `PRODUCT_ORDER` is
  // the current catalog, so a row for a retired product (BRAINSTORM, after the
  // room was deleted) was simply not rendered — and an admin asked to clear it
  // had no control anywhere in the app that could. A held row is listed whether
  // or not the bundle still knows the product's name; `grantable` already keeps
  // the grant button off it.
  const listed: Product[] = [
    ...PRODUCT_ORDER,
    ...(rows ?? []).map((r) => r.product).filter((p) => !PRODUCT_ORDER.includes(p)),
  ];
  const availabilityOf = (product: Product) =>
    catalog.find((c) => c.product === product)?.availability ?? 'PUBLIC';
  // Sunsetting keeps its holders and takes no new ones; retired takes nobody.
  const grantable = (product: Product) => ['PUBLIC', 'PRIVATE'].includes(availabilityOf(product));
  const hasTerms = (r: AdminEntitlement) =>
    r.monthly_fee_usd !== null || r.allowance_override !== null || r.rate_multiplier !== 1 || r.overage_policy === 'BILL';

  return (
    <div className="grid gap-6 md:grid-cols-2 text-sm" data-testid="user-access">
      {error && <p className="md:col-span-2 text-red-600 dark:text-red-400">{error}</p>}

      <section>
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Products</h4>
        <ul className="space-y-2">
          {listed.map((product: Product) => {
            const row = byProduct.get(product);
            const name = PRODUCT_INFO[product]?.name ?? product;
            return (
              <li key={product}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-800 dark:text-gray-200">
                    {name}
                    {availabilityOf(product) !== 'PUBLIC' && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
                        {availabilityOf(product).toLowerCase()}
                      </span>
                    )}
                    {row && !row.active && (
                      <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">{row.status}</span>
                    )}
                    {/* WHOSE ROW THIS IS. Revoking a Stripe-backed row does not
                        cancel anything — the next webhook writes it straight
                        back and the customer carries on being billed for what
                        they were just cut off from. Cancelling belongs in the
                        Portal or the Stripe dashboard. */}
                    {row?.stripe_backed && (
                      <span
                        className="ml-2 text-[10px] uppercase tracking-widest text-indigo-600 dark:text-indigo-300"
                        title={
                          row.current_period_end
                            ? `Paid subscription, renews ${new Date(row.current_period_end).toLocaleDateString()}. Cancel in Stripe, not here.`
                            : 'Paid subscription. Cancel in Stripe, not here.'
                        }
                      >
                        stripe
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    {row?.active ? (
                      <>
                        <select
                          aria-label={`${name} plan`}
                          value={row.plan}
                          disabled={busy}
                          onChange={(e) =>
                            act(() => api.adminGrantEntitlement(userId, { product, plan: e.target.value as PlanKey }))
                          }
                          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1 py-0.5 text-xs"
                        >
                          {PLAN_KEYS.map((k) => (
                            <option key={k} value={k}>
                              {k}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setTermsOpen(termsOpen === product ? null : product)}
                          className={`text-xs hover:underline ${hasTerms(row) ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400'}`}
                          title="Contract terms: fee, included credits, discount, overage"
                        >
                          {hasTerms(row) ? 'Contract' : 'Terms'}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            // A Stripe-backed row is not ours to revoke: this
                            // marks it canceled here, the subscription keeps
                            // running, and the next webhook writes the row back
                            // — so the customer is billed for something they
                            // can no longer open. Say so before it happens.
                            if (
                              row.stripe_backed &&
                              !window.confirm(
                                `${name} is a paid Stripe subscription. Revoking here does NOT cancel it - ` +
                                  'the next webhook will restore the row and billing continues. ' +
                                  'Cancel it in Stripe first. Revoke anyway?',
                              )
                            ) {
                              return;
                            }
                            act(() => api.adminRevokeEntitlement(userId, product));
                          }}
                          className="text-xs text-red-700 dark:text-red-300 hover:underline disabled:opacity-50"
                        >
                          Revoke
                        </button>
                      </>
                    ) : grantable(product) ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => act(() => api.adminGrantEntitlement(userId, { product, plan: 'free' }))}
                        className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-blue-400 disabled:opacity-50"
                      >
                        Grant (free)
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">no new users</span>
                    )}
                  </span>
                </div>
                {row?.active && hasTerms(row) && termsOpen !== product && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {row.monthly_fee_usd !== null && `$${row.monthly_fee_usd}/mo · `}
                    {row.allowance_override !== null && `${formatCredits(row.allowance_override)} credits/mo · `}
                    {row.rate_multiplier !== 1 && `×${row.rate_multiplier} rates · `}
                    {row.overage_policy === 'BILL'
                      ? `overage billed${row.overage_cap_credits !== null ? ` up to ${formatCredits(row.overage_cap_credits)}` : ''}`
                      : 'stops at zero'}
                  </p>
                )}
                {row?.active && termsOpen === product && (
                  <TermsEditor
                    row={row}
                    busy={busy}
                    onSave={async (patch, clear) => {
                      const body: AdminEntitlementGrant = { product, ...patch, clear };
                      await act(() => api.adminGrantEntitlement(userId, body));
                      setTermsOpen(null);
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Credits</h4>
        {credits ? (
          <>
            <p className="text-gray-800 dark:text-gray-200 tabular-nums">
              <span className="text-xl font-semibold">{formatCredits(credits.credits.balance)}</span> left ·{' '}
              {formatCredits(credits.credits.plan_credits)} of {formatCredits(credits.credits.allowance)} this month ·{' '}
              {formatCredits(credits.credits.bank_credits)} banked
              {credits.credits.period_end && ` · resets ${resetsOn(credits.credits.period_end)}`}
            </p>
            {credits.credits.overage_credits > 0 && (
              <p className="mt-1 text-xs text-amber-800 dark:text-amber-200" data-testid="overage-line">
                Over by {formatCredits(credits.credits.overage_credits)} credits · ${credits.overage_usd.toFixed(2)} at $
                {credits.overage_usd_per_credit}/credit
                {credits.credits.overage_cap !== null && ` · cap ${formatCredits(credits.credits.overage_cap)}`}{' '}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm(`Mark $${credits.overage_usd.toFixed(2)} of overage as invoiced? The balance returns to zero.`)) return;
                    void act(() => api.adminSettleOverage(userId));
                  }}
                  className="underline hover:no-underline disabled:opacity-50"
                >
                  Mark invoiced
                </button>
              </p>
            )}
            {!credits.reconciled && (
              <p className="text-xs text-red-600 dark:text-red-400">
                Ledger and balance disagree by {credits.drift} - a bug report, not a rounding error.
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                aria-label="Credits to add or remove"
                type="number"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="+200"
                className="w-20 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-xs"
              />
              <input
                aria-label="Reason"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="why (required)"
                className="flex-1 min-w-[10rem] border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-xs"
              />
              <button
                type="button"
                disabled={busy || !Number(amount) || !note.trim()}
                onClick={adjust}
                className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Adjust
              </button>
            </div>
            {credits.ledger.length > 0 && (
              <ul className="mt-3 space-y-0.5 text-xs text-gray-600 dark:text-gray-300 max-h-40 overflow-y-auto">
                {credits.ledger.slice(0, 12).map((r) => (
                  <li key={r.id} className="flex justify-between gap-3">
                    <span className="truncate">
                      {r.kind === 'CHARGE' ? (r.action ?? '').toLowerCase().replace(/_/g, ' ') : r.kind.toLowerCase().replace(/_/g, ' ')}
                      {r.byok ? ' (own)' : ''}
                      {r.note ? ` · ${r.note}` : ''}
                    </span>
                    <span className="tabular-nums whitespace-nowrap">
                      {r.credits > 0 ? '+' : ''}
                      {formatCredits(r.credits)}
                      {r.cost_usd !== null && r.cost_usd !== undefined && (
                        <span className="ml-1 text-gray-400" title="what it cost us">
                          (${r.cost_usd.toFixed(3)})
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">Loading…</p>
        )}
      </section>
    </div>
  );
}
